import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Typography,
  Chip,
  Alert,
  Box,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { formatMeters, formatSignedMeters, formatTimeOnly } from "../../utils/fdcHelpers";
import {
  tacticalAccordionSx,
  tacticalSummarySx,
  tacticalChipSx,
  tableHeadCellSx as headCellSx,
  tableBodyCellSx as bodyCellSx,
} from "../../utils/fdcTheme";

export default function UnprocessedObservationsPanel({
  expanded,
  onChange,
  hasMission,
  observations,
  loading,
  removeObservation,
  ptlMissing,
  activeObservationId,
  onSetActiveObservation,
}) {
  const handleSetActive = (obsId) => {
    if (onSetActiveObservation) {
      onSetActiveObservation(activeObservationId === obsId ? null : obsId);
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      disabled={!hasMission}
      disableGutters
      sx={(theme) => tacticalAccordionSx(theme)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={(theme) => tacticalSummarySx(theme)}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
            Observations
          </Typography>
          <Chip
            size="small"
            label={`${observations.length} unprocessed`}
            sx={(theme) => tacticalChipSx(theme, observations.length ? "warning" : "neutral")}
          />
          {loading && (
            <Chip
              size="small"
              label="Processing…"
              sx={(theme) => tacticalChipSx(theme, "warning")}
            />
          )}
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        {!observations.length ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="info" sx={{ fontWeight: 600 }}>
              No unprocessed observations. Fire elements will go out as initial / repeat fire.
            </Alert>
          </Box>
        ) : (
          <Stack spacing={0}>
            {ptlMissing && (
              <Alert severity="warning" sx={{ m: 1, fontWeight: 600 }}>
                PTL is mandatory before any observation can be applied.
              </Alert>
            )}
            <Box sx={(t) => ({
              px: 1.25,
              py: 1,
              borderBottom: "1px solid",
              borderColor: alpha(t.palette.divider, 0.9),
              bgcolor: alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.06 : 0.04),
            })}>
              <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.4, color: "text.secondary", textTransform: "uppercase" }}>
                Pick the observation to use as the next correction. Apply &amp; Fire dispatches it to the regulating gun (effective target) and to every gun selected in the Fire Control bar.
              </Typography>
            </Box>

            <Box sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <Table size="small" sx={{ "& td, & th": { borderBottom: 0 }, minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={(t) => ({ ...headCellSx(t), width: 56 })}>Use</TableCell>
                  <TableCell sx={headCellSx}>Obs</TableCell>
                  <TableCell sx={headCellSx}>Time</TableCell>
                  <TableCell sx={headCellSx} align="right">ΔE</TableCell>
                  <TableCell sx={headCellSx} align="right">ΔN</TableCell>
                  <TableCell sx={headCellSx} align="right">Dist</TableCell>
                  <TableCell sx={headCellSx} align="right">Alt</TableCell>
                  <TableCell sx={(t) => ({ ...headCellSx(t), width: 48 })}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {observations.map((obs) => {
                  const metrics = obs.displayMetrics || {};
                  const isActive = activeObservationId === obs.id;
                  return (
                    <TableRow
                      key={obs.id}
                      hover
                      onClick={() => handleSetActive(obs.id)}
                      sx={(t) => ({
                        cursor: "pointer",
                        "& td:first-of-type": isActive ? {
                          borderLeft: "3px solid",
                          borderLeftColor: t.palette.warning.main,
                        } : {},
                        bgcolor: isActive ? alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.1 : 0.06) : undefined,
                      })}
                    >
                      <TableCell sx={(t) => ({ ...bodyCellSx(t), p: 0.5 })} onClick={(e) => e.stopPropagation()}>
                        <Radio
                          checked={isActive}
                          onChange={() => handleSetActive(obs.id)}
                          size="medium"
                          sx={{ p: 0.5, "&.Mui-checked": { color: "warning.main" } }}
                        />
                      </TableCell>
                      <TableCell sx={(t) => ({ ...bodyCellSx(t), fontWeight: 700 })}>
                        #{obs.shot_id}
                      </TableCell>
                      <TableCell sx={(t) => ({ ...bodyCellSx(t), color: t.palette.text.secondary })}>
                        {formatTimeOnly(obs.created_at)}
                      </TableCell>
                      <TableCell sx={bodyCellSx} align="right">{formatSignedMeters(metrics.deltaE)}</TableCell>
                      <TableCell sx={bodyCellSx} align="right">{formatSignedMeters(metrics.deltaN)}</TableCell>
                      <TableCell sx={bodyCellSx} align="right">{formatMeters(metrics.distance)}</TableCell>
                      <TableCell sx={bodyCellSx} align="right">{formatMeters(obs.observed_z)}</TableCell>
                      <TableCell sx={(t) => ({ ...bodyCellSx(t), p: 0.5 })} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Discard observation" arrow>
                          <IconButton
                            size="small"
                            onClick={() => removeObservation(obs.id, obs.shot_id)}
                            sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </Box>
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
